import Button from '@cloudscape-design/components/button';
import type { ReactNode } from 'react';

/**
 * Slim top nav for public surfaces. Links + Sign in + Create tenant. Sits
 * on top of the dark hero, so its own background is transparent and text
 * is light. Sticky at scroll — falls back to a solid backdrop as the hero
 * scrolls out (via CSS backdrop-filter for browsers that support it).
 */
export interface PublicNavProps {
  brand: string;
  links: readonly { label: string; href: string }[];
  signIn: { label: string; href: string };
  signUp: { label: string; href: string };
}

export function PublicNav({ brand, links, signIn, signUp }: PublicNavProps): ReactNode {
  return (
    <nav
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 17, 23, 0.72)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <a
          href="/"
          style={{
            color: '#f8fafc',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: -0.3,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <VexturaMark />
          {brand}
        </a>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              style={{
                color: '#cbd5e1',
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                padding: '8px 12px',
                borderRadius: 6,
              }}
            >
              {l.label}
            </a>
          ))}
          <a href={signIn.href} style={{ textDecoration: 'none' }}>
            <Button variant="link">{signIn.label}</Button>
          </a>
          <a href={signUp.href} style={{ textDecoration: 'none' }}>
            <Button variant="primary">{signUp.label}</Button>
          </a>
        </div>
      </div>
    </nav>
  );
}

function VexturaMark(): ReactNode {
  return (
    <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden>
      <defs>
        <linearGradient id="vex-grad" x1="0" y1="0" x2="32" y2="32">
          <stop stopColor="#818cf8" />
          <stop offset="1" stopColor="#6366f1" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="24" height="24" rx="6" fill="url(#vex-grad)" />
      <path
        d="M11 11 L16 21 L21 11"
        stroke="white"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
